import {
  type Tag,
  addDaysToDate,
  formatDayName,
  formatDayNumber,
  formatMonth,
  generateDateRange,
} from "@taskfortress/core"
import React, { useCallback, useMemo, useRef } from "react"
import { FlatList, View, useWindowDimensions } from "react-native"
import { useData } from "../../providers/DataProvider"
import { AddTaskButton } from "../AddTaskButton"
import { Box } from "../Box"
import { Text } from "../Text"
import { TaskItem } from "./TaskItem"
import {
  MOBILE_DAYS_RANGE,
  MOBILE_EMPTY_STATE_HEIGHT,
  MOBILE_HEADER_HEIGHT,
  MOBILE_SECTION_MARGIN,
  MOBILE_TASK_HEIGHT,
  SCROLL_THROTTLE,
} from "./constants"
import { type DaySection, createDaySections } from "./taskGrouping"

interface TaskListMobileProps {
  currentDate: string
  onCurrentDateChange: (date: string) => void
  selectedTag?: Tag | null
}

// Number of days to show before and after current date is now imported from constants

export const TaskListMobile: React.FC<TaskListMobileProps> = ({
  currentDate,
  onCurrentDateChange,
  selectedTag,
}) => {
  const { tasks, tasksForSelectedTag } = useData()
  const flatListRef = useRef<FlatList>(null)
  const { height: viewportHeight } = useWindowDimensions()

  // Generate date range centered around current date
  const dateRange = useMemo(() => {
    const startDate = addDaysToDate(currentDate, -MOBILE_DAYS_RANGE)
    const endDate = addDaysToDate(currentDate, MOBILE_DAYS_RANGE)
    return generateDateRange(startDate, endDate)
  }, [currentDate])

  // Group tasks by date and create day sections
  const daySections = useMemo(() => {
    return createDaySections(dateRange, tasksForSelectedTag)
  }, [dateRange, tasksForSelectedTag])

  // Group all tasks by date and create day sections (unfiltered)
  const allDaySections = useMemo(() => {
    return createDaySections(dateRange, tasks)
  }, [dateRange, tasks])

  // Find initial scroll index to center on current date
  const initialScrollIndex = useMemo(() => {
    return dateRange.findIndex((date) => date === currentDate)
  }, [dateRange, currentDate])

  // Calculate section height for scroll calculations
  const getSectionHeight = useCallback((section: DaySection) => {
    const taskCount = section.tasks.length

    if (taskCount > 0) {
      return (
        MOBILE_HEADER_HEIGHT +
        taskCount * MOBILE_TASK_HEIGHT +
        MOBILE_SECTION_MARGIN
      )
    } else {
      return (
        MOBILE_HEADER_HEIGHT + MOBILE_EMPTY_STATE_HEIGHT + MOBILE_SECTION_MARGIN
      )
    }
  }, [])

  // Handle scroll to update current date
  const handleScroll = useCallback(
    (event: any) => {
      const { contentOffset } = event.nativeEvent
      const scrollY = contentOffset.y

      // Calculate which day section is currently most visible
      let accumulatedHeight = 0
      let bestMatch = daySections[0]?.date || currentDate
      let bestVisibility = 0

      for (let i = 0; i < daySections.length; i++) {
        const section = daySections[i]
        const sectionHeight = getSectionHeight(section)
        const sectionTop = accumulatedHeight
        const sectionBottom = accumulatedHeight + sectionHeight

        // Calculate how much of this section is visible
        const visibleTop = Math.max(scrollY, sectionTop)
        const visibleBottom = Math.min(scrollY + viewportHeight, sectionBottom)
        const visibleHeight = Math.max(0, visibleBottom - visibleTop)
        const visibilityRatio =
          visibleHeight / Math.min(sectionHeight, viewportHeight)

        if (visibilityRatio > bestVisibility) {
          bestVisibility = visibilityRatio
          bestMatch = section.date
        }

        accumulatedHeight += sectionHeight
      }

      if (bestMatch !== currentDate) {
        onCurrentDateChange(bestMatch)
      }
    },
    [
      daySections,
      currentDate,
      onCurrentDateChange,
      getSectionHeight,
      viewportHeight,
    ],
  )

  // Mobile day section component
  const MobileDaySection = ({ section }: { section: DaySection }) => {
    const allTasksForDate =
      allDaySections.find((s) => s.date === section.date)?.tasks || []
    const hiddenTasksCount = allTasksForDate.length - section.tasks.length

    return (
      <Box className="mb-4">
        <Box className="flex-row items-center justify-between px-4 py-3 bg-background-50 border-b border-outline-200">
          <Box>
            <Text className="text-lg font-semibold text-typography-900">
              {formatDayName(section.date)} {formatDayNumber(section.date)}
            </Text>
            <Text className="text-sm text-typography-600">
              {formatMonth(section.date)}
            </Text>
          </Box>
          <AddTaskButton date={section.date} selectedTag={selectedTag} small />
        </Box>

        {hiddenTasksCount > 0 && (
          <Box className="px-4 pt-2 pb-1">
            <Text className="text-xs text-typography-400 italic">
              {hiddenTasksCount} task{hiddenTasksCount === 1 ? "" : "s"} hidden
            </Text>
          </Box>
        )}

        {section.tasks.length > 0 ? (
          <Box className={hiddenTasksCount > 0 ? "px-4 pb-2" : "px-4 pt-2"}>
            {section.tasks.map((task) => (
              <TaskItem key={task.id} task={task} />
            ))}
          </Box>
        ) : (
          <Box className="px-4 py-4">
            <Text className="text-typography-500 text-center text-sm">
              No tasks for this day
            </Text>
          </Box>
        )}
      </Box>
    )
  }

  // Render day section
  const renderDaySection = ({ item }: { item: DaySection }) => (
    <View>
      <MobileDaySection section={item} />
    </View>
  )

  return (
    <FlatList
      ref={flatListRef}
      data={daySections}
      keyExtractor={(item) => item.date}
      renderItem={renderDaySection}
      className="flex-1"
      showsVerticalScrollIndicator={false}
      onScroll={handleScroll}
      scrollEventThrottle={SCROLL_THROTTLE}
      initialScrollIndex={initialScrollIndex}
      getItemLayout={(data, index) => {
        const section = data?.[index]
        if (!section) return { length: 0, offset: 0, index }

        let offset = 0
        for (let i = 0; i < index; i++) {
          offset += getSectionHeight(data[i])
        }

        return {
          length: getSectionHeight(section),
          offset,
          index,
        }
      }}
    />
  )
}
